/**
 * "Describe your ring" — natural-language → Ring Builder design patch.
 *
 * Ported from `ui/engagement/aiDesign.ts` and retargeted at the merged v37
 * state: one prompt now drives the WHOLE ring, not just the engagement half.
 * A patch field maps to exactly one `RingBuilderApi` setter (see `AiFab`'s
 * `applyPatch`) — band fields to the band half, `centerStone`/`sideStones`/
 * `head`/`engagement` to the engagement block (whose presence also means
 * "turn the head on").
 *
 * Two modes, one output shape:
 *  - **rule** (always works): a client-side keyword/regex intent parser. Covers
 *    the common StoneAlgo-style prompts ("Make the band bezel set with a halo",
 *    "3 carat oval on a thin yellow gold cathedral band with hidden halo").
 *  - **endpoint / relay** (strict upgrade): when `VITE_AI_ENDPOINT` is set we POST
 *    the prompt + a system prompt embedding the RingState schema and ask Claude
 *    for the same JSON patch. Parsed defensively; ANY failure (network, non-JSON,
 *    bad shape) falls back to the rule parser so the feature never dies.
 */
import type {
  BandStyle,
  CenterStoneConfig,
  CenterStoneShape,
  DiamondConfig,
  DiamondStyle,
  DivisionStyle,
  EdgeStyle,
  EngagementBlock,
  GrooveConfig,
  GroovePattern,
  HeadConfig,
  HeadUnderstructure,
  JointType,
  ProngCount,
  ProngTip,
  RingShape,
  ShankStyle,
  SideStonesConfig,
  SplitMode,
  StoneLayout,
  TwistConfig,
} from "../../shared/api-contract";

export type AiMode = "rule" | "endpoint" | "relay";

/**
 * Leaf-level design patch. Flat band scalars mirror the one-argument band
 * setters; sub-objects mirror the patch-style setters they are handed to.
 * Every field is optional and every value is validated by {@link sanitizePatch}
 * before it can reach an RPC.
 */
export interface AiPatch {
  // ── Band half ─────────────────────────────────────────────────────────────
  /** Cross-section preset id ("A"…"AB"). */
  profileId?: string;
  ringSizeUS?: number;
  widthMM?: number;
  thicknessMM?: number;
  edgeStyle?: EdgeStyle;
  bandStyle?: BandStyle;
  twist?: Partial<TwistConfig>;
  splitMode?: SplitMode;
  splitPatternId?: string;
  /** Metal preset id applied to every band partition (and the head). */
  metalPresetId?: string;
  /** Surface finish id applied alongside `metalPresetId`. */
  finishId?: string;
  grooves?: Partial<GrooveConfig>;
  /** The band's melee run (was the engagement patch's `shankDiamondStyle`). */
  diamond?: Partial<Pick<DiamondConfig, "style" | "stoneSizeMM" | "count" | "spreadDeg">>;
  engravingText?: string;
  /** One of the Engrave tab's font families. */
  engravingFontFamily?: string;
  // v37 band geometry
  shape?: RingShape;
  division?: DivisionStyle;
  comfortFitMM?: number;

  // ── Engagement half — ANY of these attaches the head assembly ─────────────
  engagement?: Partial<Pick<EngagementBlock, "style" | "cathedral" | "fit">>;
  centerStone?: Partial<CenterStoneConfig>;
  sideStones?: Partial<SideStonesConfig>;
  head?: Partial<HeadConfig>;
}

export interface AiResult {
  patch: AiPatch;
  /** Human sentence describing what was applied (shown in the result card). */
  summary: string;
  mode: AiMode;
}

/** Setting styles that only exist on a v37 viewer (`AiFab` drops them below that). */
export const V37_DIAMOND_STYLES = new Set<DiamondStyle>([
  "eye", "cross-bezel", "cross-channel", "cross-around", "free-scatter",
]);

// ─── Allowed value sets (the sanitizer's whitelist) ─────────────────────────

const SHAPES = [
  "round", "oval", "princess", "emerald", "cushion", "cushion-elongated",
  "radiant", "radiant-elongated", "pear", "marquise", "asscher", "heart",
] as const;
const ORIGINS = ["natural", "lab", "skip"] as const;
const LAYOUTS = ["one", "two", "three"] as const;
const UNDERSTRUCTURES = [
  "none", "basket", "pave-basket", "halo", "hidden-halo", "double-halo", "bezel",
] as const;
const PRONG_COUNTS = ["4-classic", "4-compass", "6", "8"] as const;
const PRONG_TIPS = [
  "claw", "petite-claw", "rounded", "tab", "double-claw", "double-petite-claw",
] as const;
const SHANK_STYLES = ["round", "square", "knife-edge", "split", "twist"] as const;
const FITS = ["comfort", "standard"] as const;
const EDGE_STYLES = ["sharp", "rounded", "bevel", "milgrain"] as const;
const BAND_STYLES = ["standard", "crossover"] as const;
const SPLIT_MODES = ["none", "two-tone", "tricolor", "quad", "multi"] as const;
const GROOVE_PATTERNS = ["none", "single", "double", "triple", "offset"] as const;
const JOINT_TYPES = ["v60", "v120", "uround", "convexu", "square", "milgrain", "milgrain2"] as const;
const RING_SHAPES = ["classic", "oval", "cushion", "hexagon", "wave", "octagon", "wavy"] as const;
const DIVISIONS = ["vertical", "axial", "diagonal", "wavy"] as const;
// All 23 DiamondStyle ids. This list had drifted to 19: "vertical", "clamp",
// "clamp-angled" and "free-place" were added to the contract in v39/v40 and
// never mirrored here, so the AI designer silently could not produce them and
// its own validator rejected them if a model guessed one.
const DIAMOND_STYLES = [
  "none", "rubbed-in", "side-entry", "waste", "side-shift", "channel",
  "canal-across", "side-channel", "tension-1", "tension-2", "free-stones",
  "prong-4", "prong-6", "memoir",
  "eye", "cross-bezel", "cross-channel", "cross-around", "free-scatter",
  "vertical", "clamp", "clamp-angled", "free-place",
] as const;
const METAL_PRESETS = ["yellowGold", "roseGold", "whiteGold", "platinum", "silver"] as const;
const FINISHES = [
  "polished", "brushed", "satin", "hammered", "milgrain", "ice", "nature",
  "sand", "matte", "mirror", "florentine", "wave", "sandblast",
] as const;
/** The Engrave tab's family list — a canvas font name, never a CSS stack. */
const FONT_FAMILIES = [
  "Georgia", "Times New Roman", "Garamond", "Helvetica", "Verdana",
  "Courier New", "Brush Script MT",
] as const;

/** Engraving is the band's decal (v41: 100 chars), not the engagement extras' 14. */
const ENGRAVING_MAX = 100;

// ─── Keyword tables ─────────────────────────────────────────────────────────

/** v37 silhouette. Matched (and consumed) BEFORE the centre-stone shapes so
 * "oval band" is a round-stone-agnostic band shape, not an oval diamond. */
const SILHOUETTE_RULES: { re: RegExp; value: RingShape }[] = [
  { re: /\bhexagon(?:al)?[\s-](?:shaped\s+)?(?:band|ring|shank|silhouette)\b/, value: "hexagon" },
  { re: /\boctagon(?:al)?[\s-](?:shaped\s+)?(?:band|ring|shank|silhouette)\b/, value: "octagon" },
  // Before the shared wav(e|y) rule, so "wavy band" is the finer ripple.
  { re: /\bwavy[\s-](?:shaped\s+)?(?:band|ring|shank|silhouette)\b/, value: "wavy" },
  { re: /\bcushion[\s-](?:shaped\s+)?(?:band|ring|shank|silhouette)\b/, value: "cushion" },
  { re: /\boval[\s-](?:shaped\s+)?(?:band|ring|shank|silhouette)\b/, value: "oval" },
  { re: /\bwav(?:e|y)[\s-](?:shaped\s+)?(?:band|ring|shank|silhouette)\b/, value: "wave" },
  { re: /\bclassic (?:round )?(?:band|silhouette)\b/, value: "classic" },
];

/** Ordered longest-phrase-first so "cushion elongated" wins over "cushion". */
const SHAPE_RULES: { re: RegExp; shape: CenterStoneShape }[] = [
  { re: /\bcushion[\s-]?elongated\b/, shape: "cushion-elongated" },
  { re: /\bradiant[\s-]?elongated\b|\belongated radiant\b/, shape: "radiant-elongated" },
  { re: /\brounds?\b|\bbrilliant\b/, shape: "round" },
  { re: /\bovals?\b/, shape: "oval" },
  { re: /\bprincess\b/, shape: "princess" },
  { re: /\bemeralds?\b/, shape: "emerald" },
  { re: /\bcushions?\b/, shape: "cushion" },
  { re: /\bradiants?\b/, shape: "radiant" },
  { re: /\bpears?\b|\bteardrops?\b/, shape: "pear" },
  { re: /\bmarquises?\b|\bnavette\b/, shape: "marquise" },
  { re: /\basschers?\b/, shape: "asscher" },
  { re: /\bhearts?\b/, shape: "heart" },
];

/** Compound metals must be tested before the bare "gold" fallback. */
const METAL_RULES: { re: RegExp; presetId: string }[] = [
  { re: /\bwhite gold\b|\bwhite[\s-]?gold\b/, presetId: "whiteGold" },
  { re: /\b(rose|pink|red) gold\b/, presetId: "roseGold" },
  { re: /\byellow gold\b/, presetId: "yellowGold" },
  { re: /\bplatinum\b|\bplat\b/, presetId: "platinum" },
  { re: /\b(sterling )?silver\b/, presetId: "silver" },
  { re: /\byellow\b/, presetId: "yellowGold" },
  { re: /\brose\b/, presetId: "roseGold" },
  { re: /\bgold\b/, presetId: "yellowGold" },
];

/** Surface finish. "milgrain" is deliberately absent — as a bare word it reads
 * as the milgrain EDGE far more often than the finish (see EDGE_RULES). */
const FINISH_RULES: { re: RegExp; id: string }[] = [
  { re: /\bsand[\s-]?blast(ed)?\b/, id: "sandblast" },
  { re: /\bbrushed\b/, id: "brushed" },
  { re: /\bsatin\b/, id: "satin" },
  { re: /\bhammered\b/, id: "hammered" },
  { re: /\bflorentine\b/, id: "florentine" },
  { re: /\bmatte\b|\bmatt\b/, id: "matte" },
  { re: /\bmirror[\s-]?(finish|polish)?\b/, id: "mirror" },
  { re: /\bpolished\b|\bhigh[\s-]polish\b/, id: "polished" },
];

/** Cross-section profile presets (`ProfilePresets.ts` ids). */
const PROFILE_RULES: { re: RegExp; id: string }[] = [
  { re: /\bd[\s-]?shape[d]?\b/, id: "A" },
  { re: /\bslight court\b/, id: "B" },
  { re: /\bheavy court\b/, id: "D" },
  { re: /\bflat court\b/, id: "F" },
  { re: /\boval court\b/, id: "O" },
  { re: /\bcourt\b/, id: "C" },
  { re: /\bhalf[\s-]?round\b|\bpipe\b/, id: "R" },
  { re: /\bknife[\s-]?edge\b/, id: "I" },
  { re: /\beuro shank\b/, id: "J" },
  { re: /\bconcave\b/, id: "K" },
  { re: /\bhigh dome[d]?\b/, id: "N" },
  { re: /\b(low )?dome[d]?\b/, id: "M" },
  { re: /\bdouble bevel\b/, id: "T" },
  { re: /\bwide bevel\b/, id: "S" },
  { re: /\breverse bevel\b/, id: "V" },
  { re: /\btrue square\b|\bsquare (band|shank|profile)\b/, id: "U" },
  { re: /\bflat (band|shank|profile)\b/, id: "E" },
];

/**
 * Edge treatment. Needs the word "edge".
 *
 * "milgrain" is NOT here any more. It used to be the first rule, so the bare
 * word produced `edgeStyle: "milgrain"` — which is a shape in the band's
 * cross-section, a raised ridge, and not a bead in sight. Asking for milgrain
 * and getting a ridge was the single most likely way to meet the wrong one of
 * the four unrelated things this tree calls "Milgrain". It now routes to the
 * real swept beads below.
 */
const EDGE_RULES: { re: RegExp; value: EdgeStyle }[] = [
  { re: /\bbevel(l?ed)? edge\b|\bchamfer(ed)? edge\b/, value: "bevel" },
  { re: /\bround(ed)? edge\b|\bsoft edge\b/, value: "rounded" },
  { re: /\bsharp edge\b|\bcrisp edge\b/, value: "sharp" },
];

const GROOVE_RULES: { re: RegExp; value: GroovePattern }[] = [
  { re: /\bdouble groove[s]?\b|\btwo groove[s]?\b/, value: "double" },
  { re: /\btriple groove[s]?\b|\bthree groove[s]?\b/, value: "triple" },
  { re: /\boffset groove[s]?\b/, value: "offset" },
  { re: /\bno groove[s]?\b|\bungrooved\b/, value: "none" },
  { re: /\bgroove[sd]?\b|\bchannel(l?ed) line\b/, value: "single" },
];

/** v37 joint (groove cross-section). */
const JOINT_RULES: { re: RegExp; value: JointType }[] = [
  { re: /\bv[\s-]?60\b|\bnarrow v\b/, value: "v60" },
  { re: /\bv[\s-]?120\b|\bwide v\b/, value: "v120" },
  { re: /\bconvex[\s-]?u\b/, value: "convexu" },
  { re: /\bu[\s-]?(round|shaped?)\b|\bround groove\b/, value: "uround" },
  { re: /\bsquare groove\b|\bflat[\s-]?bottom(ed)? groove\b/, value: "square" },
  { re: /\bv[\s-]?(groove|joint|cut)\b/, value: "v60" },
];

/** v37 split-seam path. */
const DIVISION_RULES: { re: RegExp; value: DivisionStyle }[] = [
  { re: /\bdiagonal (split|seam|divide)\b|\bdiagonal(ly)? split\b/, value: "diagonal" },
  { re: /\bwav(e|y) (split|seam|divide)\b/, value: "wavy" },
  { re: /\baxial (split|seam|divide)\b|\bside[\s-]by[\s-]side split\b/, value: "axial" },
  { re: /\bvertical (split|seam|divide)\b/, value: "vertical" },
];

/** Head understructure — longest phrases first (hidden/double halo before halo). */
const UNDERSTRUCTURE_RULES: { re: RegExp; value: HeadUnderstructure }[] = [
  { re: /\bhidden[\s-]?halo\b/, value: "hidden-halo" },
  { re: /\bdouble[\s-]?halo\b/, value: "double-halo" },
  { re: /\bpav[eé][\s-]?basket\b/, value: "pave-basket" },
  { re: /\bbezel([\s-]?set(ting)?)?\b/, value: "bezel" },
  { re: /\bhalos?\b/, value: "halo" },
  { re: /\bbaskets?\b/, value: "basket" },
];

const PRONG_COUNT_RULES: { re: RegExp; value: ProngCount }[] = [
  { re: /\bcompass\b|\b4[\s-]?compass\b/, value: "4-compass" },
  { re: /\b(6|six)[\s-]?prong\b/, value: "6" },
  { re: /\b(8|eight)[\s-]?prong\b/, value: "8" },
  { re: /\b(4|four)[\s-]?prong\b/, value: "4-classic" },
];

const PRONG_TIP_RULES: { re: RegExp; value: ProngTip }[] = [
  { re: /\bdouble[\s-]?petite[\s-]?claws?\b/, value: "double-petite-claw" },
  { re: /\bdouble[\s-]?claws?\b/, value: "double-claw" },
  { re: /\bpetite[\s-]?claws?\b/, value: "petite-claw" },
  { re: /\brounded (prong|tip)/, value: "rounded" },
  { re: /\btab (prong|tip|set)/, value: "tab" },
  { re: /\bclaw (prong|tip)|\bclaws?\b/, value: "claw" },
];

/** Engagement shank silhouette. Requires the noun so "split" alone stays a
 * metal split and "twisted band" stays the band-half crossover construction. */
const SHANK_STYLE_RULES: { re: RegExp; value: ShankStyle }[] = [
  { re: /\bsplit[\s-]?shank\b/, value: "split" },
  { re: /\btwist(ed)?[\s-]?shank\b/, value: "twist" },
  { re: /\bknife[\s-]?edge[\s-]?shank\b/, value: "knife-edge" },
  { re: /\bsquare shank\b/, value: "square" },
  { re: /\bround shank\b/, value: "round" },
];

const LAYOUT_RULES: { re: RegExp; value: StoneLayout }[] = [
  { re: /\b(three|3)[\s-]?stone\b|\btrilogy\b/, value: "three" },
  { re: /\b(two|2)[\s-]?stone\b|\btoi et moi\b/, value: "two" },
  { re: /\bsolitaire\b|\bsingle stone\b/, value: "one" },
];

const FONT_RULES: { re: RegExp; id: string }[] = [
  { re: /\bcursive\b|\bscript\b|\bhandwritten\b/, id: "Brush Script MT" },
  { re: /\bgaramond\b/, id: "Garamond" },
  { re: /\btimes\b/, id: "Times New Roman" },
  { re: /\bcourier\b|\btypewriter\b/, id: "Courier New" },
  { re: /\bverdana\b/, id: "Verdana" },
  { re: /\bgeorgia\b|\bserif\b/, id: "Georgia" },
  { re: /\bblock\b|\bsans[\s-]?serif\b|\bhelvetica\b/, id: "Helvetica" },
];

// ─── Rule-based parser ──────────────────────────────────────────────────────

/** Parse a free-text prompt into a design patch + summary, fully client-side. */
export function parsePromptRuleBased(prompt: string): AiResult {
  let t = ` ${prompt.toLowerCase()} `;
  const patch: AiPatch = {};
  const notes: string[] = [];

  // ── v37 silhouette. Consumed from the working text: "oval band" is a band
  // outline, and must not also read as an oval centre stone. ────────────────
  for (const r of SILHOUETTE_RULES) {
    if (r.re.test(t)) {
      patch.shape = r.value;
      t = t.replace(r.re, " ");
      if (r.value !== "classic") notes.push(`${r.value} silhouette`);
      break;
    }
  }

  // ── Centre stone ─────────────────────────────────────────────────────────
  // Carat: "2 carat", "2ct", "1.5 carats", "0.75 ct"
  const caratMatch = t.match(/(\d+(?:\.\d+)?)\s*(?:ct\b|carats?\b)/);
  if (caratMatch) {
    const ct = clamp(parseFloat(caratMatch[1]), 0.25, 10);
    patch.centerStone = { ...patch.centerStone, caratCt: ct };
    notes.push(`${ct}ct center`);
  }

  for (const r of SHAPE_RULES) {
    if (r.re.test(t)) {
      patch.centerStone = { ...patch.centerStone, shape: r.shape };
      notes.push(`${r.shape.replace("-", " ")} cut`);
      break;
    }
  }

  if (/\blab[\s-]?grown\b|\blab diamond\b|\blab\b/.test(t)) {
    patch.centerStone = { ...patch.centerStone, origin: "lab" };
    notes.push("lab-grown");
  } else if (/\bnatural\b|\bmined\b/.test(t)) {
    patch.centerStone = { ...patch.centerStone, origin: "natural" };
    notes.push("natural");
  }

  // ── Metal + finish (head and every band partition) ───────────────────────
  for (const r of METAL_RULES) {
    if (r.re.test(t)) { patch.metalPresetId = r.presetId; notes.push(metalLabel(r.presetId)); break; }
  }
  for (const r of FINISH_RULES) {
    if (r.re.test(t)) { patch.finishId = r.id; notes.push(`${r.id} finish`); break; }
  }

  // Multi-metal split + v37 seam path.
  if (/\btri[\s-]?colou?r\b|\bthree[\s-]?tone\b/.test(t)) {
    patch.splitMode = "tricolor";
    patch.splitPatternId = "3t-1:1:1";
    notes.push("tricolor");
  } else if (/\btwo[\s-]?tone\b|\bbi[\s-]?colou?r\b|\bmixed metal\b/.test(t)) {
    patch.splitMode = "two-tone";
    patch.splitPatternId = "2t-1:1";
    notes.push("two-tone");
  }
  for (const r of DIVISION_RULES) {
    if (r.re.test(t)) { patch.division = r.value; notes.push(`${r.value} seam`); break; }
  }

  // ── Head ─────────────────────────────────────────────────────────────────
  for (const r of UNDERSTRUCTURE_RULES) {
    if (r.re.test(t)) {
      patch.head = { ...patch.head, understructure: r.value };
      notes.push(`${r.value.replace("-", " ")} setting`);
      break;
    }
  }
  // "plain" / "no halo" resets the head to a bare setting (unless a bezel/halo
  // was explicitly asked for above).
  if (!patch.head?.understructure && /\bno halo\b|\bplain (head|setting)\b|\bsimple setting\b/.test(t)) {
    patch.head = { ...patch.head, understructure: "none" };
    notes.push("plain setting");
  }

  for (const r of PRONG_COUNT_RULES) {
    if (r.re.test(t)) { patch.head = { ...patch.head, prongCount: r.value }; notes.push(`${r.value} prongs`); break; }
  }
  for (const r of PRONG_TIP_RULES) {
    if (r.re.test(t)) { patch.head = { ...patch.head, prongTip: r.value }; notes.push(`${r.value.replace("-", " ")} tips`); break; }
  }
  if (/\bprong pav[eé]\b|\bpav[eé] prong/.test(t)) {
    patch.head = { ...patch.head, prongPave: true };
    notes.push("pavé prongs");
  }
  const haloSize = t.match(/(\d+(?:\.\d+)?)\s*mm halo\b/);
  if (haloSize) patch.head = { ...patch.head, haloStoneSizeMM: clamp(parseFloat(haloSize[1]), 0.8, 1.5) };

  // ── Engagement shank silhouette / cathedral ──────────────────────────────
  for (const r of SHANK_STYLE_RULES) {
    if (r.re.test(t)) { patch.engagement = { ...patch.engagement, style: r.value }; notes.push(`${r.value.replace("-", " ")} shank`); break; }
  }
  if (/\bcathedral\b/.test(t)) {
    patch.engagement = { ...patch.engagement, cathedral: true };
    notes.push("cathedral");
  } else if (/\bnon[\s-]?cathedral\b/.test(t)) {
    patch.engagement = { ...patch.engagement, cathedral: false };
  }

  // ── Band construction: cross-section, crossover twist, edge, grooves ──────
  for (const r of PROFILE_RULES) {
    if (r.re.test(t)) { patch.profileId = r.id; break; }
  }
  if (/\btwist(ed)?\b|\brope\b|\bbraid(ed)?\b|\bwoven\b|\bcross[\s-]?over\b/.test(t)) {
    patch.bandStyle = "crossover";
    notes.push("cross-over twist");
    const strands = t.match(/\b(\d)\s*strand/);
    if (strands) patch.twist = { ...patch.twist, strands: clamp(parseInt(strands[1], 10), 1, 3) };
    const twists = t.match(/\b(\d+)\s*twists?\b/);
    if (twists) patch.twist = { ...patch.twist, twists: clamp(parseInt(twists[1], 10), 2, 12) };
  }
  for (const r of EDGE_RULES) {
    if (r.re.test(t)) { patch.edgeStyle = r.value; notes.push(`${r.value} edges`); break; }
  }
  // "milgrain" / "beaded edge" ⇒ REAL beads swept along both band edges, which
  // is what the words mean to a jeweller. `edges` needs a bead joint type to do
  // anything, so both are set together.
  if (/\bmilgrain\b|\bbead(ed)? edge\b/.test(t)) {
    patch.grooves = { ...patch.grooves, jointType: "milgrain", edges: true };
    notes.push("milgrain beads on both edges");
  }
  for (const r of GROOVE_RULES) {
    if (r.re.test(t)) { patch.grooves = { ...patch.grooves, pattern: r.value }; if (r.value !== "none") notes.push(`${r.value} groove`); break; }
  }
  for (const r of JOINT_RULES) {
    if (r.re.test(t)) { patch.grooves = { ...patch.grooves, jointType: r.value }; break; }
  }
  if (/\bseam groove[s]?\b/.test(t)) patch.grooves = { ...patch.grooves, seam: true };
  if (/\bedge groove[s]?\b/.test(t)) patch.grooves = { ...patch.grooves, edges: true };

  // ── Dimensions ───────────────────────────────────────────────────────────
  const widthMM = t.match(/(\d+(?:\.\d+)?)\s*mm\s*(?:wide|width|band\b)/);
  const thickMM = t.match(/(\d+(?:\.\d+)?)\s*mm\s*(?:thick|thickness|deep)/);
  if (widthMM) {
    patch.widthMM = clamp(parseFloat(widthMM[1]), 1.5, 12);
    notes.push(`${patch.widthMM}mm wide`);
  } else if (/\bthin\b|\bdelicate\b|\bslim\b|\bpetite band\b/.test(t)) {
    patch.widthMM = 1.8;
    notes.push("thin band");
  } else if (/\b(thick|wide|bold|chunky|substantial)\b/.test(t)) {
    patch.widthMM = 3.2;
    notes.push("wide band");
  } else if (/\bmedium (band|width)\b/.test(t)) {
    patch.widthMM = 2.5;
  }
  if (thickMM) patch.thicknessMM = clamp(parseFloat(thickMM[1]), 0.8, 4);

  // Ring size — "size 6", "size 6.5"
  const sizeMatch = t.match(/\bsize (\d+(?:\.5)?)\b/);
  if (sizeMatch) {
    patch.ringSizeUS = clamp(parseFloat(sizeMatch[1]), 3, 13.5);
    notes.push(`size ${patch.ringSizeUS}`);
  }

  // v37 comfort fit — the inner-curve bulge (the engagement `fit` flag is a
  // separate, engagement-only field the endpoint may still send).
  if (/\bcomfort fit\b|\bcomfort[\s-]?fit\b/.test(t)) {
    patch.comfortFitMM = 0.5;
    notes.push("comfort fit");
  } else if (/\bstandard fit\b|\bflat inside\b/.test(t)) {
    patch.comfortFitMM = 0;
  }

  // ── Band melee run ───────────────────────────────────────────────────────
  // Clear-intent first, then set-intent.
  if (/\bno pav[eé]\b|\bplain band\b|\bplain platinum\b|\bplain gold\b|\bsolitaire band\b|\bunadorned\b|\bno diamonds? on the band\b/.test(t)) {
    patch.diamond = { ...patch.diamond, style: "none" };
    notes.push("plain band (no pavé)");
  } else if (/\bchannel[\s-]?set\b|\bchannel band\b|\bchannel\b/.test(t)) {
    patch.diamond = { ...patch.diamond, style: "channel" };
    notes.push("channel-set band");
  } else if (/\bscatter(ed)?\b|\bconfetti\b/.test(t)) {
    patch.diamond = { ...patch.diamond, style: "free-scatter" };
    notes.push("scattered stones");
  } else if (/\beternity\b|\bfull circle\b/.test(t)) {
    patch.diamond = { ...patch.diamond, style: "rubbed-in", count: 0 };
    notes.push("eternity band");
  } else if (/\bpav[eé]\b|\bmicro[\s-]?pav[eé]\b|\bdiamond band\b|\baccent(ed)? band\b/.test(t)) {
    patch.diamond = { ...patch.diamond, style: "rubbed-in" };
    notes.push("pavé band");
  }
  const stoneCount = t.match(/\b(\d+)\s*(?:small |accent |melee )?stones?\b/);
  if (stoneCount && !/\b(one|two|three)[\s-]?stone\b/.test(t)) {
    patch.diamond = { ...patch.diamond, count: clamp(parseInt(stoneCount[1], 10), 0, 60) };
  }
  const stoneSize = t.match(/(\d+(?:\.\d+)?)\s*mm\s*(?:stones?|diamonds?|melee)\b/);
  if (stoneSize) patch.diamond = { ...patch.diamond, stoneSizeMM: clamp(parseFloat(stoneSize[1]), 0.5, 4) };

  // ── Multi-stone layout ───────────────────────────────────────────────────
  for (const r of LAYOUT_RULES) {
    if (r.re.test(t)) { patch.sideStones = { ...patch.sideStones, layout: r.value }; if (r.value !== "one") notes.push(`${r.value}-stone`); break; }
  }

  // ── Engraving — "engrave X", 'engraved with "X"', "engraving: X" ─────────
  const engrave = prompt.match(/engrav(?:ed|ing|e)\s*(?:it\s+)?(?:with\s+)?(?:the words?\s+)?(?:that says?\s+)?[:"']?\s*([^"'.,;]+?)\s*["']?\s*(?:\.|,|;|$)/i);
  if (engrave && engrave[1] && engrave[1].trim().length && engrave[1].trim().length <= ENGRAVING_MAX) {
    const text = engrave[1].trim().slice(0, ENGRAVING_MAX);
    patch.engravingText = text;
    notes.push(`engraved "${text}"`);
    for (const r of FONT_RULES) {
      if (r.re.test(t)) { patch.engravingFontFamily = r.id; break; }
    }
  }

  const summary = notes.length
    ? `Designed a ${notes.join(", ")}.`
    : "I couldn't pick out specific details — try naming a shape, carat, metal, or setting.";

  return { patch, summary, mode: "rule" };
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function metalLabel(presetId: string): string {
  switch (presetId) {
    case "yellowGold": return "yellow gold";
    case "roseGold": return "rose gold";
    case "whiteGold": return "white gold";
    case "platinum": return "platinum";
    case "silver": return "silver";
    default: return presetId;
  }
}

// ─── Claude endpoint mode (strict upgrade when configured) ──────────────────

const AI_ENDPOINT = import.meta.env.VITE_AI_ENDPOINT as string | undefined;

/** Whether a server relay/endpoint is configured. If not, we're always rule-based. */
export function hasAiEndpoint(): boolean {
  return typeof AI_ENDPOINT === "string" && AI_ENDPOINT.trim().length > 0;
}

const SYSTEM_PROMPT = `You are a jewelry design assistant for a ring configurator that builds ONE ring:
a wedding band, optionally with an engagement head attached. Turn the user's natural-language
description into a JSON patch. Respond with ONLY a single JSON object, no prose, no markdown fences.

Shape:
{
  "patch": {
    "profileId"?: "A".."W",            // cross-section: A D-Shape, C Court, E Flat, G Beveled, I Knife Edge, M Low Dome, U True Square
    "ringSizeUS"?: number(3-13.5),
    "widthMM"?: number(1.5-12),
    "thicknessMM"?: number(0.8-4),
    "edgeStyle"?: "sharp"|"rounded"|"bevel"|"milgrain",
    "bandStyle"?: "standard"|"crossover",
    "twist"?: { "strands"?: 1|2|3, "twists"?: number(2-12), "strandRadiusMM"?: number(0.5-2), "amplitudeMM"?: number(0-1.6) },
    "splitMode"?: "none"|"two-tone"|"tricolor"|"quad"|"multi",
    "splitPatternId"?: "2t-1:1"|"2t-1:2"|"3t-1:1:1"|...,
    "metalPresetId"?: "yellowGold"|"roseGold"|"whiteGold"|"platinum"|"silver",
    "finishId"?: "polished"|"brushed"|"satin"|"hammered"|"matte"|"mirror"|"sandblast"|"hammered"|"florentine",
    "grooves"?: { "pattern"?: "none"|"single"|"double"|"triple"|"offset", "depthMM"?: number(0.05-0.8),
                  "widthMM"?: number(0.2-2),
                  "jointType"?: "v60"|"v120"|"uround"|"convexu"|"square"|"milgrain"|"milgrain2",
                  "seam"?: boolean, "edges"?: boolean },
    "diamond"?: { "style"?: DiamondStyle, "stoneSizeMM"?: number(0.5-4), "count"?: number(0-60) },
    "engravingText"?: string(<=100 chars),
    "engravingFontFamily"?: "Georgia"|"Times New Roman"|"Garamond"|"Helvetica"|"Verdana"|"Courier New"|"Brush Script MT",
    "shape"?: "classic"|"oval"|"cushion"|"hexagon"|"wave"|"octagon"|"wavy",  // ring outline down the finger
    "division"?: "vertical"|"axial"|"diagonal"|"wavy",          // path of the multi-metal seam
    "comfortFitMM"?: number(0-1),                              // inner-curve bulge, 0 = flat inside

    // Any of the four fields below ATTACHES the engagement head. Omit them all for a plain band.
    "engagement"?: { "style"?: "round"|"square"|"knife-edge"|"split"|"twist", "cathedral"?: boolean, "fit"?: "comfort"|"standard" },
    "centerStone"?: { "shape"?: Shape, "caratCt"?: number(0.25-10), "origin"?: "natural"|"lab"|"skip" },
    "sideStones"?: { "layout"?: "one"|"two"|"three", "shape"?: Shape, "caratRatio"?: number(0.25-1) },
    "head"?: { "understructure"?: Understructure, "prongCount"?: "4-classic"|"4-compass"|"6"|"8",
               "prongTip"?: "claw"|"petite-claw"|"rounded"|"tab"|"double-claw"|"double-petite-claw",
               "prongPave"?: boolean, "haloStoneSizeMM"?: number(0.8-1.5), "bezelWallMM"?: number(0.5-1) }
  },
  "summary": "one friendly sentence describing what you designed"
}

Shape = "round"|"oval"|"princess"|"emerald"|"cushion"|"cushion-elongated"|"radiant"|"radiant-elongated"|"pear"|"marquise"|"asscher"|"heart"
Understructure = "none"|"basket"|"pave-basket"|"halo"|"hidden-halo"|"double-halo"|"bezel"
DiamondStyle = "none"|"rubbed-in"|"side-entry"|"waste"|"side-shift"|"channel"|"canal-across"|"side-channel"|"tension-1"|"tension-2"|"free-stones"|"prong-4"|"prong-6"|"memoir"|"eye"|"cross-bezel"|"cross-channel"|"cross-around"|"free-scatter"|"vertical"|"clamp"|"clamp-angled"|"free-place"

Rules: metalPresetId + finishId apply to the head AND every band partition. Diamonds ON THE BAND are
"diamond" ("pavé band" => style "rubbed-in", "channel band" => "channel", "eternity" => "rubbed-in" with
count 0 = auto-fill, "plain band" => "none"); the centre stone is "centerStone". Omit any field the user
didn't imply.`;

/**
 * POST the prompt to the configured endpoint (a server relay that holds the
 * Claude key). Expected response: either `{ patch, summary }` directly, or a
 * Claude Messages response whose text content is that JSON. Any failure throws
 * so the caller falls back to the rule parser.
 */
async function requestEndpoint(prompt: string): Promise<AiResult> {
  const res = await fetch(AI_ENDPOINT as string, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(localStorage.getItem("auth_token") ? { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } : {}),
    },
    body: JSON.stringify({
      system: SYSTEM_PROMPT,
      prompt,
      model: "claude-3-5-haiku-latest",
      max_tokens: 512,
      temperature: 0,
    }),
  });
  if (!res.ok) throw new Error(`AI endpoint ${res.status}`);
  const raw = await res.json();
  const parsed = coerceAiResult(raw);
  if (!parsed) throw new Error("AI endpoint returned an unparseable payload");
  return { ...parsed, mode: "endpoint" };
}

/** Pull a `{ patch, summary }` out of whatever the endpoint returned, defensively. */
function coerceAiResult(raw: unknown): Omit<AiResult, "mode"> | null {
  // Direct { patch, summary }
  const direct = asPatchResult(raw);
  if (direct) return direct;

  // Claude Messages API: { content: [{ type:"text", text:"..." }] }
  const obj = raw as { content?: { type?: string; text?: string }[]; text?: string };
  const text =
    (Array.isArray(obj?.content) ? obj.content.find((c) => c?.type === "text")?.text : undefined) ??
    obj?.text;
  if (typeof text === "string") {
    const json = extractJson(text);
    if (json) return asPatchResult(json);
  }
  return null;
}

function asPatchResult(v: unknown): Omit<AiResult, "mode"> | null {
  if (!v || typeof v !== "object") return null;
  const o = v as { patch?: unknown; summary?: unknown };
  if (o.patch && typeof o.patch === "object") {
    return {
      patch: sanitizePatch(o.patch as Record<string, unknown>),
      summary: typeof o.summary === "string" ? o.summary : "Applied your design.",
    };
  }
  return null;
}

/** Extract the first balanced JSON object from a string (strips code fences/prose). */
function extractJson(text: string): unknown | null {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    return null;
  }
}

// ─── Whitelist sanitizer ────────────────────────────────────────────────────
// A model-produced patch never reaches an RPC unvalidated: unknown keys are
// dropped, enums must be a member of their allowed set, numbers are clamped
// into the same range the corresponding control uses.

function pick<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}
function num(v: unknown, min: number, max: number): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? clamp(v, min, max) : undefined;
}
function bool(v: unknown): boolean | undefined {
  return typeof v === "boolean" ? v : undefined;
}
/** Keep an object only if the sanitizer kept at least one field. */
function keep<T extends object>(o: T): T | undefined {
  return Object.keys(o).length ? o : undefined;
}
function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}
function set<T extends object, K extends keyof T>(target: T, key: K, value: T[K] | undefined): void {
  if (value !== undefined) target[key] = value;
}

/** Whitelist patch fields so a hallucinated key or value can never reach a setter. */
export function sanitizePatch(raw: Record<string, unknown>): AiPatch {
  const p = obj(raw);
  const out: AiPatch = {};

  // Band scalars.
  if (typeof p.profileId === "string" && /^[A-Z]{1,2}$/.test(p.profileId)) out.profileId = p.profileId;
  set(out, "ringSizeUS", num(p.ringSizeUS, 3, 13.5));
  set(out, "widthMM", num(p.widthMM, 1.5, 12));
  set(out, "thicknessMM", num(p.thicknessMM, 0.8, 4));
  set(out, "edgeStyle", pick(p.edgeStyle, EDGE_STYLES));
  set(out, "bandStyle", pick(p.bandStyle, BAND_STYLES));
  set(out, "splitMode", pick(p.splitMode, SPLIT_MODES));
  // v42: quad/multi ids ("4t-…", "5t-…") and the generated even ones ("n6-even").
  if (typeof p.splitPatternId === "string" && /^([2-5]t|n[1-8])-[a-z0-9:]{1,14}$/.test(p.splitPatternId)) {
    out.splitPatternId = p.splitPatternId;
  }
  set(out, "metalPresetId", pick(p.metalPresetId, METAL_PRESETS));
  set(out, "finishId", pick(p.finishId, FINISHES));
  set(out, "shape", pick(p.shape, RING_SHAPES));
  set(out, "division", pick(p.division, DIVISIONS));
  set(out, "comfortFitMM", num(p.comfortFitMM, 0, 1));
  if (typeof p.engravingText === "string") {
    // The engraving is drawn onto a canvas decal — strip control characters.
    out.engravingText = p.engravingText.replace(/[\u0000-\u001f\u007f]/g, "").slice(0, ENGRAVING_MAX);
  }
  set(out, "engravingFontFamily", pick(p.engravingFontFamily, FONT_FAMILIES));

  const twist: Partial<TwistConfig> = {};
  const tw = obj(p.twist);
  set(twist, "strands", num(tw.strands, 1, 3));
  set(twist, "twists", num(tw.twists, 2, 12));
  set(twist, "strandRadiusMM", num(tw.strandRadiusMM, 0.5, 2));
  set(twist, "amplitudeMM", num(tw.amplitudeMM, 0, 1.6));
  set(out, "twist", keep(twist));

  const grooves: Partial<GrooveConfig> = {};
  const gr = obj(p.grooves);
  set(grooves, "pattern", pick(gr.pattern, GROOVE_PATTERNS));
  set(grooves, "depthMM", num(gr.depthMM, 0.05, 0.8));
  set(grooves, "widthMM", num(gr.widthMM, 0.2, 2));
  set(grooves, "jointType", pick(gr.jointType, JOINT_TYPES));
  set(grooves, "seam", bool(gr.seam));
  set(grooves, "edges", bool(gr.edges));
  set(out, "grooves", keep(grooves));

  const diamond: NonNullable<AiPatch["diamond"]> = {};
  const dm = obj(p.diamond);
  set(diamond, "style", pick(dm.style, DIAMOND_STYLES));
  set(diamond, "stoneSizeMM", num(dm.stoneSizeMM, 0.5, 4));
  set(diamond, "count", num(dm.count, 0, 60));
  set(diamond, "spreadDeg", num(dm.spreadDeg, 0, 360));
  set(out, "diamond", keep(diamond));

  // Engagement half.
  const engagement: NonNullable<AiPatch["engagement"]> = {};
  const en = obj(p.engagement);
  set(engagement, "style", pick(en.style, SHANK_STYLES));
  set(engagement, "cathedral", bool(en.cathedral));
  set(engagement, "fit", pick(en.fit, FITS));
  set(out, "engagement", keep(engagement));

  const center: Partial<CenterStoneConfig> = {};
  const cs = obj(p.centerStone);
  set(center, "shape", pick(cs.shape, SHAPES));
  set(center, "caratCt", num(cs.caratCt, 0.25, 10));
  set(center, "origin", pick(cs.origin, ORIGINS));
  set(out, "centerStone", keep(center));

  const sides: Partial<SideStonesConfig> = {};
  const ss = obj(p.sideStones);
  set(sides, "layout", pick(ss.layout, LAYOUTS));
  set(sides, "shape", pick(ss.shape, SHAPES));
  set(sides, "caratRatio", num(ss.caratRatio, 0.25, 1));
  set(out, "sideStones", keep(sides));

  const head: Partial<HeadConfig> = {};
  const hd = obj(p.head);
  set(head, "understructure", pick(hd.understructure, UNDERSTRUCTURES));
  set(head, "prongCount", pick(hd.prongCount, PRONG_COUNTS));
  set(head, "prongTip", pick(hd.prongTip, PRONG_TIPS));
  set(head, "prongPave", bool(hd.prongPave));
  set(head, "haloStoneSizeMM", num(hd.haloStoneSizeMM, 0.8, 1.5));
  set(head, "bezelWallMM", num(hd.bezelWallMM, 0.5, 1));
  set(out, "head", keep(head));

  return out;
}

/**
 * Public entry: describe → design. Uses the Claude endpoint when configured;
 * on ANY failure (or when no endpoint is set) falls back to the rule parser.
 */
export async function designFromPrompt(prompt: string): Promise<AiResult> {
  if (hasAiEndpoint()) {
    try {
      return await requestEndpoint(prompt);
    } catch (err) {
      console.warn("[aiDesign] endpoint failed, falling back to rule parser:", err);
    }
  }
  return parsePromptRuleBased(prompt);
}
